import { useQuery } from "@tanstack/react-query";
import { Flame, Trophy, Target } from "lucide-react";
import { fetchMyReputation } from "@/lib/predictions/predictionReputation";
import { reputationLevelFor } from "@/lib/predictions/reputationLevels";
import { useLanguage } from "@/hooks/useLanguage";

/**
 * Compact prediction reputation card: level + emoji, total points,
 * current streak, exact predictions, and progress to next tier.
 * Renders nothing if the user is anonymous.
 */
export function PredictionReputationBadge({ compact = false }: { compact?: boolean }) {
  const { t } = useLanguage();
  const { data } = useQuery({
    queryKey: ["prediction-reputation", "me"],
    queryFn: () => fetchMyReputation(),
    staleTime: 30_000,
  });

  if (!data) return null;
  const { current, next, progress } = reputationLevelFor(data.total_points);
  const accuracy = data.predictions_total > 0
    ? Math.round((data.exact_predictions / data.predictions_total) * 100)
    : 0;

  if (compact) {
    return (
      <div className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background/80 px-2.5 py-1 text-[11px] font-bold">
        <span>{current.emoji}</span>
        <span style={{ color: current.color }}>{current.label}</span>
        <span className="text-muted-foreground">· {data.total_points} pts</span>
        {data.streak_current > 1 && (
          <span className="inline-flex items-center gap-0.5 text-amber-500">
            <Flame className="h-3 w-3" /> {data.streak_current}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="fan-identity-frame rounded-2xl bg-gradient-to-br from-background to-accent/10 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-2xl" aria-hidden>{current.emoji}</span>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              {t("reputation_level_label")}
            </div>
            <div className="text-base font-black" style={{ color: current.color }}>
              {current.label}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-black">{data.total_points}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {t("reputation_points")}
          </div>
        </div>
      </div>

      {next && (
        <div className="mt-3">
          <div className="h-1.5 overflow-hidden rounded-full bg-accent/30">
            <div
              className="h-full rounded-full transition-[width]"
              style={{ width: `${progress}%`, background: current.color }}
            />
          </div>
          <div className="mt-1 text-[10px] text-muted-foreground">
            {t("reputation_to_next")
              .replace("{n}", String(Math.max(0, next.min - data.total_points)))
              .replace("{label}", next.label)}
          </div>
        </div>
      )}

      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-accent/20 p-2">
          <Flame className="mx-auto h-4 w-4 text-amber-500" />
          <div className="text-sm font-bold">{data.streak_current}</div>
          <div className="text-[9px] uppercase text-muted-foreground">{t("reputation_streak")}</div>
        </div>
        <div className="rounded-lg bg-accent/20 p-2">
          <Target className="mx-auto h-4 w-4 text-emerald-500" />
          <div className="text-sm font-bold">{data.exact_predictions}</div>
          <div className="text-[9px] uppercase text-muted-foreground">{t("reputation_exact")}</div>
        </div>
        <div className="rounded-lg bg-accent/20 p-2">
          <Trophy className="mx-auto h-4 w-4 text-primary" />
          <div className="text-sm font-bold">{accuracy}%</div>
          <div className="text-[9px] uppercase text-muted-foreground">{t("reputation_accuracy")}</div>
        </div>
      </div>
    </div>
  );
}